/**
 * Image citation JSON interchange.
 *
 * The image block's APA panel can round-trip its citation fields through a small
 * JSON envelope. The point of the envelope is that a teacher can hand the
 * example file to an AI assistant, get a filled-in copy back, and drop it into
 * the modal without retyping anything — so the example doubles as the
 * specification and the format has to survive whatever shape the assistant
 * returns (bare object, bare array, or the full envelope).
 *
 * Everything here is plain text. Values land in <input> fields, never in
 * innerHTML, but they are still untrusted input from a downloaded file, so each
 * value is trimmed, stripped of control characters and length-capped.
 */

import { REFERENCE_CATEGORY_VALUES } from '../lib/apaImageReference';

export const IMAGE_CITATION_JSON_KIND = 'lessonplannerone/image-citations';
export const IMAGE_CITATION_JSON_VERSION = 1;

export const IMAGE_CITATION_SOURCE_TYPES = ['image', 'table', 'clipart_builtin', 'clipart_external'];

const MAX_TEXT_LENGTH = 2000;
const MAX_ENTRIES = 50;

// Field aliases, because an assistant asked for "the citation" tends to answer
// with the words a librarian would use rather than the keys this app stores.
const TEXT_FIELD_ALIASES = {
  alt: ['alt', 'altText', 'alt_text', 'description'],
  caption: ['caption', 'figureCaption'],
  title: ['title', 'imageTitle', 'figureTitle', 'workTitle'],
  author: ['author', 'creator', 'artist', 'photographer', 'authors'],
  source: ['source', 'publisher', 'site', 'siteName', 'website', 'container'],
  url: ['url', 'link', 'sourceUrl', 'pageUrl'],
  date: ['date', 'publicationDate', 'published', 'year'],
  page: ['page', 'pages', 'pageNumber'],
  license: ['license', 'copyright', 'rights', 'licence'],
  tableNumber: ['tableNumber', 'table_number', 'tableNo'],
  // Reference-entry only. `referenceCategory` chooses the reference template,
  // `medium` names the format in square brackets, and `referenceTitle` carries
  // the sentence-case title when it differs from the Title Case one the slide
  // note shows.
  referenceCategory: ['referenceCategory', 'reference_category', 'referenceTemplate', 'category'],
  medium: ['medium', 'imageFormat', 'image_format', 'format', 'mediumDescriptor'],
  referenceTitle: ['referenceTitle', 'reference_title', 'sentenceCaseTitle'],
  articleTitle: ['articleTitle', 'article_title', 'article']
};

const BOOLEAN_FIELD_ALIASES = {
  showCaption: ['showCaption', 'captionOnSlide'],
  includePage: ['includePage', 'showPage'],
  includeLicense: ['includeLicense', 'showLicense'],
  includeCitation: ['includeCitation', 'addToReferenceList', 'showCitation'],
  isAdapted: ['isAdapted', 'adapted']
};

const SOURCE_TYPE_ALIASES = {
  image: 'image',
  figure: 'image',
  photo: 'image',
  photograph: 'image',
  illustration: 'image',
  diagram: 'image',
  chart: 'image',
  table: 'table',
  clipart: 'clipart_external',
  clipart_builtin: 'clipart_builtin',
  'clipart-builtin': 'clipart_builtin',
  builtin: 'clipart_builtin',
  clipart_external: 'clipart_external',
  'clipart-external': 'clipart_external',
  external: 'clipart_external'
};

const REFERENCE_CATEGORY_ALIASES = {
  site: 'site_image',
  database: 'site_image',
  website: 'site_image',
  image: 'site_image',
  untitled: 'untitled_image',
  no_title: 'untitled_image',
  no_author: 'no_author_image',
  museum: 'museum_artwork',
  artwork: 'museum_artwork',
  magazine: 'magazine_image',
  film_still: 'film',
  movie: 'film',
  webpage: 'webpage_source',
  book: 'book_source',
  article: 'article_source',
  journal: 'article_source'
};

// Keys that describe the file rather than a citation, so `citations` can be
// omitted and a bare object still read as one entry.
const ENVELOPE_ONLY_KEYS = new Set([
  '$schema',
  'kind',
  'version',
  'readMeFirst',
  'fieldGuide',
  'exportedAt',
  'citations',
  'images'
]);

const cleanText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  if (typeof value !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, MAX_TEXT_LENGTH);
};

const readAliased = (entry, aliases) => {
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(entry, alias)) {
      const cleaned = cleanText(entry[alias]);
      if (cleaned) return cleaned;
    }
  }
  return '';
};

const readAliasedBoolean = (entry, aliases) => {
  for (const alias of aliases) {
    if (!Object.prototype.hasOwnProperty.call(entry, alias)) continue;
    const raw = entry[alias];
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase();
      if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
      if (['false', 'no', 'n', '0'].includes(normalized)) return false;
    }
  }
  return undefined;
};

export const normalizeCitationSourceType = (value) => {
  const cleaned = cleanText(value).toLowerCase().replace(/\s+/g, '_');
  return SOURCE_TYPE_ALIASES[cleaned] || 'image';
};

/**
 * The reference template, as a value the formatter recognises. An
 * unknown word is dropped rather than guessed at — `inferReferenceCategory`
 * then picks one from the fields themselves.
 */
export const normalizeReferenceCategory = (value) => {
  const cleaned = cleanText(value).toLowerCase().replace(/[\s-]+/g, '_');
  if (!cleaned) return '';
  const aliased = REFERENCE_CATEGORY_ALIASES[cleaned] || cleaned;
  return REFERENCE_CATEGORY_VALUES.includes(aliased) ? aliased : '';
};

/**
 * A date has to satisfy an <input type="date">, which only renders YYYY-MM-DD.
 * A year on its own still produces a correct APA year, so it is kept rather
 * than invented into a full date — the field simply looks empty in the picker.
 */
const normalizeCitationDate = (value) => {
  const cleaned = cleanText(value);
  if (!cleaned) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) return cleaned;
  const isoPrefix = cleaned.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoPrefix) return isoPrefix[1];
  const yearOnly = cleaned.match(/^(\d{4})(-\d{2})?$/);
  if (yearOnly) return cleaned;
  const yearInText = cleaned.match(/\b(1[0-9]{3}|20[0-9]{2})\b/);
  if (yearInText) return yearInText[1];
  return '';
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const looksLikeCitationEntry = (entry) => {
  if (!isPlainObject(entry)) return false;
  return Object.keys(entry).some((key) => !ENVELOPE_ONLY_KEYS.has(key));
};

/** Normalize one loosely-shaped object into the fields the image form stores. */
export const normalizeImageCitationEntry = (rawEntry = {}) => {
  const entry = isPlainObject(rawEntry) ? rawEntry : {};
  const normalized = {
    sourceType: normalizeCitationSourceType(entry.sourceType ?? entry.type ?? entry.kind)
  };

  Object.entries(TEXT_FIELD_ALIASES).forEach(([field, aliases]) => {
    normalized[field] = readAliased(entry, aliases);
  });
  normalized.date = normalizeCitationDate(normalized.date);
  normalized.referenceCategory = normalizeReferenceCategory(normalized.referenceCategory);

  Object.entries(BOOLEAN_FIELD_ALIASES).forEach(([field, aliases]) => {
    const value = readAliasedBoolean(entry, aliases);
    if (value !== undefined) normalized[field] = value;
  });

  // Defaults match the form's own defaults: everything included unless the file
  // says otherwise, and empty optional fields are not worth advertising.
  if (normalized.includeCitation === undefined) normalized.includeCitation = true;
  if (normalized.showCaption === undefined) normalized.showCaption = true;
  if (normalized.includePage === undefined) normalized.includePage = Boolean(normalized.page);
  if (normalized.includeLicense === undefined) normalized.includeLicense = Boolean(normalized.license);
  if (normalized.isAdapted === undefined) normalized.isAdapted = false;

  return normalized;
};

/**
 * Pull citation entries out of whatever an assistant handed back.
 * Accepts the full envelope, `{ citations: [...] }`, a bare array, or a single
 * bare citation object. Throws an Error whose message is safe to show in the UI.
 */
export const parseImageCitationJson = (rawJson) => {
  const text = typeof rawJson === 'string' ? rawJson.trim() : '';
  if (!text) {
    throw new Error('Paste some JSON or choose a .json file first.');
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error(`That is not valid JSON (${error.message}).`);
  }

  let entries;
  if (Array.isArray(parsed)) {
    entries = parsed;
  } else if (isPlainObject(parsed)) {
    if (Array.isArray(parsed.citations)) entries = parsed.citations;
    else if (Array.isArray(parsed.images)) entries = parsed.images;
    else if (looksLikeCitationEntry(parsed)) entries = [parsed];
    else entries = [];
  } else {
    throw new Error('Expected a JSON object or array of citation objects.');
  }

  const usable = entries.filter(looksLikeCitationEntry).slice(0, MAX_ENTRIES);
  if (usable.length === 0) {
    throw new Error('No citations found. Expected a "citations" array — download the example file to see the shape.');
  }

  return usable.map(normalizeImageCitationEntry);
};

/** Map a normalized entry onto the `image_*` / `image_<n>_*` form fields. */
export const citationEntryToFormFields = (entry, baseFieldName) => {
  const fields = {
    [`${baseFieldName}_showCaption`]: entry.showCaption !== false,
    [`${baseFieldName}_sourceType`]: entry.sourceType || 'image',
    [`${baseFieldName}_title`]: entry.title || '',
    [`${baseFieldName}_author`]: entry.author || '',
    [`${baseFieldName}_source`]: entry.source || '',
    [`${baseFieldName}_url`]: entry.url || '',
    [`${baseFieldName}_date`]: entry.date || '',
    [`${baseFieldName}_page`]: entry.page || '',
    [`${baseFieldName}_includePage`]: entry.includePage !== false,
    [`${baseFieldName}_license`]: entry.license || '',
    [`${baseFieldName}_includeLicense`]: entry.includeLicense !== false,
    [`${baseFieldName}_tableNumber`]: entry.tableNumber || '',
    [`${baseFieldName}_isAdapted`]: Boolean(entry.isAdapted),
    [`${baseFieldName}_includeCitation`]: entry.includeCitation !== false,
    [`${baseFieldName}_referenceCategory`]: entry.referenceCategory || '',
    [`${baseFieldName}_medium`]: entry.medium || '',
    [`${baseFieldName}_referenceTitle`]: entry.referenceTitle || '',
    [`${baseFieldName}_articleTitle`]: entry.articleTitle || ''
  };

  // Alt text and caption are optional in the file; an omitted value must not
  // wipe what the teacher already typed.
  if (entry.alt) fields[`${baseFieldName}_alt`] = entry.alt;
  if (entry.caption) fields[`${baseFieldName}_caption`] = entry.caption;

  return fields;
};

/** Read the current form fields for one image back out as a citation entry. */
export const formFieldsToCitationEntry = (formData = {}, baseFieldName) => ({
  alt: cleanText(formData[`${baseFieldName}_alt`]),
  caption: cleanText(formData[`${baseFieldName}_caption`]),
  showCaption: formData[`${baseFieldName}_showCaption`] !== false,
  sourceType: normalizeCitationSourceType(formData[`${baseFieldName}_sourceType`]),
  title: cleanText(formData[`${baseFieldName}_title`]),
  author: cleanText(formData[`${baseFieldName}_author`]),
  source: cleanText(formData[`${baseFieldName}_source`]),
  url: cleanText(formData[`${baseFieldName}_url`]),
  date: normalizeCitationDate(formData[`${baseFieldName}_date`]),
  page: cleanText(formData[`${baseFieldName}_page`]),
  includePage: formData[`${baseFieldName}_includePage`] !== false,
  license: cleanText(formData[`${baseFieldName}_license`]),
  includeLicense: formData[`${baseFieldName}_includeLicense`] !== false,
  tableNumber: cleanText(formData[`${baseFieldName}_tableNumber`]),
  isAdapted: Boolean(formData[`${baseFieldName}_isAdapted`]),
  includeCitation: formData[`${baseFieldName}_includeCitation`] !== false,
  referenceCategory: normalizeReferenceCategory(formData[`${baseFieldName}_referenceCategory`]),
  medium: cleanText(formData[`${baseFieldName}_medium`]),
  referenceTitle: cleanText(formData[`${baseFieldName}_referenceTitle`]),
  articleTitle: cleanText(formData[`${baseFieldName}_articleTitle`])
});

export const buildImageCitationEnvelope = (entries = []) => ({
  $schema: 'https://lessonplannerone.com/schemas/image-citation.v1.json',
  kind: IMAGE_CITATION_JSON_KIND,
  version: IMAGE_CITATION_JSON_VERSION,
  exportedAt: new Date().toISOString().slice(0, 10),
  citations: entries
});

/**
 * Field-by-field guide shipped inside the example file. The example is the
 * thing people paste into a chat, so the rules have to travel with it.
 */
export const IMAGE_CITATION_FIELD_GUIDE = {
  alt: 'string — short description of the image for screen readers. Optional; leave out to keep what is already typed.',
  caption: 'string — visible caption under the image. Optional.',
  showCaption: 'boolean — false keeps the caption text but leaves it off the slide, so only the APA note shows there. The reference list entry is unaffected.',
  sourceType: 'one of "image", "table", "clipart_builtin", "clipart_external". Use "image" for a photo, figure, chart or diagram.',
  title: 'string — title of the image, figure or table. Required for a complete citation.',
  author: 'string — creator, artist or photographer in APA order, surname first: "Goldberg, E.". For an organisation, give the full name and include the word that identifies it as one ("... Association", "... Institute", "... Archives"), or write it in capitals.',
  source: 'string — where it was published: the site, journal, book or museum. Required for a complete citation.',
  url: 'string — direct link to the page the image came from. Use "" if there is none.',
  date: 'string — publication date as YYYY-MM-DD. A bare year ("2019") is accepted when only the year is known. Use "" for no date.',
  page: 'string — page number(s) such as "45" or "45-47". Use "" for web sources.',
  includePage: 'boolean — false hides the page from the note. Set false when the source has no page.',
  license: 'string — a Creative Commons licence ("CC BY-NC 2.0", "CC0 1.0") or a public domain statement. This APA house style leaves plain copyright lines such as "© 2019 Disney" off the slide, so one entered here will not appear in the note.',
  includeLicense: 'boolean — false hides the licence from the note.',
  referenceCategory: 'string — which reference template the source falls under: "site_image", "untitled_image", "no_author_image", "museum_artwork", "magazine_image", "film", "webpage_source", "book_source" or "article_source". Leave "" to let the app choose from the other fields. Use one of the last four when the image has no artist of its own — a film still, a photo inside an article, an illustration in a book — because the underlying source is what gets referenced, not the image.',
  medium: 'string — the format named in square brackets in the reference entry: "Photograph", "Painting", "Illustration", "Infographic", "Film", … Used only in the reference list, never in the slide note. Defaults to "Image".',
  referenceTitle: 'string — the title in sentence case, for the reference entry, when it differs from the Title Case title the slide note shows ("Picture perfect: The making of Sleeping Beauty" vs "Picture Perfect: The Making of Sleeping Beauty"). Leave "" to use "title" in both places.',
  articleTitle: 'string — for "magazine_image", the article the image sits in. Otherwise "".',
  tableNumber: 'string — table number when sourceType is "table", e.g. "1". Otherwise "".',
  isAdapted: 'boolean — true when the figure or table was adapted rather than reproduced as-is.',
  includeCitation: 'boolean — true adds the source note under the image and an entry to the deck reference list.'
};

export const IMAGE_CITATION_README = [
  'This file describes APA citation details for images used in a LessonPlanner One lesson.',
  'Fill in one object inside "citations" for each image, in the same order the images appear in the modal, then save the file as .json.',
  'Keep the key names exactly as they are. Use "" for anything you genuinely do not know — do not invent authors, dates or licences.',
  'Citations follow this app\u2019s APA house style (7th ed.), not generic APA 7. Two consequences: a plain copyright line such as "© 1992 Disney" never appears on the slide, so only give "license" a Creative Commons licence or a public domain statement; and an image with no artist of its own (a film still, a photo inside an article) is referenced as its underlying source, which is what "referenceCategory" selects.',
  'Return the whole file, valid JSON, with no commentary, code fences or trailing commas.',
  'Upload it in the app: image block modal -> APA Citation -> JSON / AI -> Choose .json file.',
  'You may delete "readMeFirst" and "fieldGuide" before uploading; the app ignores them either way.'
];

/** The example file offered by the "Download example" button. */
export const buildImageCitationExample = () => ({
  $schema: 'https://lessonplannerone.com/schemas/image-citation.v1.json',
  kind: IMAGE_CITATION_JSON_KIND,
  version: IMAGE_CITATION_JSON_VERSION,
  readMeFirst: IMAGE_CITATION_README,
  fieldGuide: IMAGE_CITATION_FIELD_GUIDE,
  citations: [
    {
      alt: 'Animator Eric Goldberg sketching a character at his desk',
      caption: 'Eric Goldberg animating the Genie',
      showCaption: true,
      sourceType: 'image',
      title: 'Eric Goldberg at Work on Aladdin',
      author: 'Goldberg, E.',
      source: 'Disney Animation Archives',
      url: 'https://example.com/archives/eric-goldberg',
      date: '1992-11-25',
      page: '',
      includePage: false,
      license: 'CC BY-NC 2.0',
      includeLicense: true,
      tableNumber: '',
      isAdapted: false,
      includeCitation: true,
      referenceCategory: 'site_image',
      medium: 'Photograph',
      referenceTitle: 'Eric Goldberg at work on Aladdin',
      articleTitle: ''
    },
    {
      alt: 'Table comparing frame rates used in traditional animation',
      caption: 'Frame rate comparison',
      showCaption: false,
      sourceType: 'table',
      title: 'Frame rates in traditional animation',
      author: 'Williams, R.',
      source: 'The Animator’s Survival Kit',
      url: '',
      date: '2001',
      page: '62',
      includePage: true,
      license: '',
      includeLicense: false,
      tableNumber: '1',
      isAdapted: true,
      includeCitation: true,
      referenceCategory: 'book_source',
      medium: '',
      referenceTitle: '',
      articleTitle: ''
    }
  ]
});

/** Prompt text for the "Copy AI prompt" button. */
export const buildImageCitationPrompt = (imageCount = 1) => {
  const count = Number.isFinite(imageCount) && imageCount > 0 ? Math.floor(imageCount) : 1;
  const plural = count === 1 ? 'image' : 'images';
  return [
    `I need APA 7 citation details for ${count} ${plural} I am putting in a lesson. I will describe each one (or attach it) below.`,
    '',
    `Reply with nothing but a JSON file matching this structure, with exactly ${count} object(s) in "citations", in the order I list the ${plural}:`,
    '',
    JSON.stringify(
      {
        kind: IMAGE_CITATION_JSON_KIND,
        version: IMAGE_CITATION_JSON_VERSION,
        citations: [buildImageCitationExample().citations[0]]
      },
      null,
      2
    ),
    '',
    'Field rules:',
    ...Object.entries(IMAGE_CITATION_FIELD_GUIDE).map(([field, note]) => `- ${field}: ${note}`),
    '',
    'Use "" for anything you cannot verify — do not invent authors, dates, publishers or licences.',
    'Only Creative Commons licences and public domain statements belong in "license": this app leaves a plain copyright notice off the slide.',
    'No commentary, no markdown code fences, no trailing commas.',
    '',
    `Here ${count === 1 ? 'is the image' : 'are the images'}:`,
    '1. '
  ].join('\n');
};
